import classNames from 'classnames';
import React, { useState, useMemo, useCallback, useEffect, useRef, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import insightsClient from 'search-insights';

import { IEdp } from '../Prp/IProduct';
import { ProductHelpers } from '../utils/ProductHelpers';
import { Size } from '../utils/SizeHelpers';
import { SwatchHelpers } from '../utils/SwatchHelpers';

import { ProductCategoryType, StyleAndSize, deriveFreqAutoDelivery } from './ProductCard';
import { en, fr } from './productCardModali18n';

export interface ProductCardModalProps {
    onCloseListener: () => void;
    colorOrTasteText: string;
    styles: Array<{
        DimensionId: string;
        DisplayName: string;
    }>;
    sizes: Array<{
        DimensionId: string;
        DisplayName: string;
    }>;
    productImage: string;
    edps: Array<IEdp>;
    edp: number;
    AvailableSwatches: string[];
    //onAddToBagClickListener: (sizeIndex: number, styleIndex: number) => Promise<boolean>;
    onAddToBagClickListener: (styleIndex: number, sizeIndex: number) => void;
    productHref: string;
    categoryPath: ProductCategoryType;
    updateSizeIndex: (value: React.SetStateAction<number>) => void;
    updateColorOrTasteIndex: (value: React.SetStateAction<number>) => void;
    mainIsAddedToBag: boolean;
    updateIsAddToBagLoading: (value: React.SetStateAction<boolean>) => void;
    itemNo?: string;
    position?: number;
    currentPage?: number;
    pageSize?: number;
    queryId?: string;
    showSwatches: boolean;
    labelSize: Size;
    displayedSizeLabels: StyleAndSize[];
}

const ProductCardModal = ({
    onCloseListener,
    colorOrTasteText,
    AvailableSwatches,
    styles,
    sizes,
    productImage,
    edps,
    onAddToBagClickListener,
    productHref,
    categoryPath,
    updateSizeIndex,
    updateColorOrTasteIndex,
    mainIsAddedToBag,
    updateIsAddToBagLoading,
    itemNo,
    edp,
    position,
    currentPage,
    pageSize,
    queryId,
    showSwatches,
    labelSize,
    displayedSizeLabels,
}: ProductCardModalProps) => {
    const { i18n, t } = useTranslation('product-card-modal');
    i18n.addResourceBundle('en', 'product-card-modal', en, true, true);
    i18n.addResourceBundle('fr', 'product-card-modal', fr, true, true);

    const [selectedSizeIndex, _setSelectedSizeIndex] = useState(-1);
    const [selectedColorOrTasteIndex, _setSelectedColorOrTasteIndex] = useState(-1);
    const [isAddedToBag, setIsAddedToBag] = useState(false);
    const [isAddToBagLoading, _setIsAddToBagLoading] = useState(false);
    const IMAGE_URL_PREFIX = 'https://item.tscimg.ca/TSC/';
    const LOADING_ASSET_PATH = '/App_Assets/public/loading.gif';
    const SIZE_COUNT_LIMIT = 10;
    const STYLE_COUNT_LIMIT = 16;
    const productCardOverlay = useRef<HTMLDivElement | null>(null);

    const setSelectedSizeIndex = (value: React.SetStateAction<number>) => {
        _setSelectedSizeIndex(value);
        updateSizeIndex(value);
    };

    const setSelectedColorOrTasteIndex = (value: React.SetStateAction<number>) => {
        _setSelectedColorOrTasteIndex(value);
        updateColorOrTasteIndex(value);
    };

    const setIsAddToBagLoading = (value: React.SetStateAction<boolean>) => {
        _setIsAddToBagLoading(value);
        updateIsAddToBagLoading(value);
    };

    const buttonText = useMemo(() => {
        if (isAddedToBag) {
            return t('addedToBag');
        }

        if (styles.length === 0 && sizes.length === 0) {
            return t('addToBag');
        }

        if (styles.length === 0 && sizes.length !== 0) {
            if (selectedSizeIndex === -1) {
                return t('selectSize');
            }
        }

        if (styles.length !== 0 && sizes.length === 0) {
            if (selectedColorOrTasteIndex === -1) {
                return t('selectStyle');
            }
        }

        if (styles.length !== 0 && sizes.length !== 0) {
            if (selectedSizeIndex === -1 && selectedColorOrTasteIndex === -1) {
                return t('selectSizeAndStyle');
            }

            if (selectedSizeIndex !== -1 && selectedColorOrTasteIndex === -1) {
                return t('selectStyle');
            }

            if (selectedSizeIndex === -1 && selectedColorOrTasteIndex !== -1) {
                return t('selectSize');
            }
        }

        return t('addToBag');
    }, [isAddedToBag, selectedSizeIndex, selectedColorOrTasteIndex, styles, sizes, categoryPath]);

    function getDefaultEdpItem() {
        return edps.find((edpItem) => edpItem.EdpNo === edp);
    }

    const isFreqAutoDelivery = deriveFreqAutoDelivery(
        sizes,
        styles,
        getDefaultEdpItem,
        selectedSizeIndex,
        edps,
        selectedColorOrTasteIndex,
    );

    const ctaText = useMemo(() => {
        if (styles.length === 0 && sizes.length === 0) {
            return t('goToDetailPage');
        }

        if (styles.length === 0 && sizes.length !== 0) {
            if (selectedSizeIndex === -1) {
                return t('selectSize');
            }
        }

        if (styles.length !== 0 && sizes.length === 0) {
            if (selectedColorOrTasteIndex === -1) {
                return t('selectStyle');
            }
        }

        if (styles.length !== 0 && sizes.length !== 0) {
            if (selectedSizeIndex === -1 && selectedColorOrTasteIndex === -1) {
                return t('selectSizeAndStyle');
            }

            if (selectedSizeIndex !== -1 && selectedColorOrTasteIndex === -1) {
                return t('selectStyle');
            }

            if (selectedSizeIndex === -1 && selectedColorOrTasteIndex !== -1) {
                return t('selectSize');
            }
        }

        if (!showSwatches && labelSize === 'select') {
            return t('selectSizeAndStyle');
        }

        return t('goToDetailPage');
    }, [
        isAddedToBag,
        selectedSizeIndex,
        selectedColorOrTasteIndex,
        styles,
        sizes,
        categoryPath,
        showSwatches,
        labelSize,
    ]);

    const isSOEligible = useMemo(() => {
        let isSOEligible = false;

        for (const edp of edps) {
            if (edp.IsSOEligible == true) {
                isSOEligible = true;
                break;
            }
        }
        return isSOEligible;
    }, [edps]);

    const onSizeKeyDownListener = (e: React.KeyboardEvent<HTMLButtonElement>, i: number) => {
        if (e.key === 'Enter') {
            // console.log('%c onSizeKeyDownListener.', 'color: purple; font-weight: bold;');
            e.preventDefault();
            setSelectedSizeIndex((prev) => (prev === i ? -1 : i));
        }
    };

    const onSizeClickListener = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, i: number) => {
        e.preventDefault();
        setSelectedSizeIndex((prev) => (prev === i ? -1 : i));
    };

    const onColorOrTasteKeyDownListener = (e: React.KeyboardEvent<HTMLButtonElement>, i: number) => {
        // console.log('%c onColorOrTasteKeyDownListener.', 'color: purple; font-weight: bold;');
        if (e.key === 'Enter') {
            e.preventDefault();
            setSelectedColorOrTasteIndex((prev) => (prev === i ? -1 : i));
        }
    };

    const onColorOrTasteClickListener = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, i: number) => {
        e.preventDefault();
        setSelectedColorOrTasteIndex((prev) => (prev === i ? -1 : i));
    };

    const keyboardControls = (event: KeyboardEvent) => {
        /*
    console.log('%c event.code:', 'color: red; font-weight: bold;');
    console.log({ eventCode: event.code });
    */
        if (event.code === 'Escape') {
            event.preventDefault();
            setIsAddedToBag(false);
            onCloseListener();
            return;
        }
        const isTabPressed = event.key === 'Tab';
        if (!isTabPressed) {
            return;
        }
        const focusableElements = 'button:not([disabled]), [tabindex]:not([tabindex="-1"])';
        const firstFocusableElement = productCardOverlay?.current?.querySelectorAll(
            focusableElements,
        )[0] as HTMLElement;
        const focusableContent = productCardOverlay?.current?.querySelectorAll(focusableElements);
        /*
      console.log('%c firstFousableElement, focusableContent:', 'color: blue; font-weight: bold;');
      console.log({ firstFocusableElement, focusableContent });
      */
        if (!focusableContent) {
            return;
        }

        const lastFocusableElement = focusableContent[focusableContent.length - 1] as HTMLElement;
        if (event.shiftKey) {
            if (document.activeElement === firstFocusableElement) {
                event.preventDefault();
                lastFocusableElement.focus();
            }
        } else {
            if (document.activeElement === lastFocusableElement) {
                event.preventDefault();
                firstFocusableElement.focus();
            }
        }
    };

    useEffect(() => {
        const firstParagraph = productCardOverlay?.current?.querySelector('p[tabindex="0"]') as HTMLParagraphElement;
        firstParagraph?.focus();
    }, []);

    const onAddToBagClick = () => {
        setIsAddToBagLoading(true);
    };

    // useEffect(() => {
    //   if (isAddToBagLoading) {
    //     onAddToBagClickListener(selectedSizeIndex, selectedColorOrTasteIndex)
    //       .then(() => {
    //         setIsAddedToBag(true);
    //         setIsAddToBagLoading(false);
    //       });
    //   }
    // }, [isAddToBagLoading]);

    const trackSearchEventClick = (itemNo: string, position: number) => {
        /*
    console.log('%c insightsClientObject:', 'color: red; font-weight: bold;');
    console.log({ insightsClientObject: {
      eventName: 'Items clicked',
      index: 'products',
      objectIDs: [itemNo],
      positions: [position + 1 + (currentPage - 1) * pageSize],
      queryID: queryId ? queryId : '',
    } });
    */
        insightsClient('clickedObjectIDsAfterSearch', {
            eventName: 'Items clicked',
            index: 'products',
            objectIDs: [itemNo],
            positions: [position + 1 + (currentPage - 1) * pageSize],
            queryID: queryId ? queryId : '',
        });
    };

    const isSizeDisabled = useCallback(
        (i: number): boolean => {
            return ProductHelpers.isSizeDisabled(i, selectedColorOrTasteIndex, edps, styles, sizes);
        },
        [selectedColorOrTasteIndex, edps, styles, sizes],
    );

    const isStyleDisabled = useCallback(
        (i: number): boolean => {
            return ProductHelpers.isStyleDisabled(i, selectedSizeIndex, edps, styles, sizes);
        },
        [selectedSizeIndex, edps, styles, sizes],
    );

    const firstAvailableStyleIndex = useMemo(() => {
        for (let i = 0; i < styles.length; i++) {
            if (!isStyleDisabled(i)) {
                return i;
            }
        }

        return -1;
    }, [styles, isStyleDisabled]);

    const firstAvailableSizeIndex = useMemo(() => {
        for (let i = 0; i < sizes.length; i++) {
            if (!isSizeDisabled(i)) {
                return i;
            }
        }

        return -1;
    }, [sizes, isSizeDisabled]);

    useEffect(() => {
        if (!showSwatches) {
            setSelectedColorOrTasteIndex(firstAvailableStyleIndex);
        }

        if (labelSize === 'select') {
            setSelectedSizeIndex(firstAvailableSizeIndex);
        }
    }, []);

    const getSwatchUrl = (index: number) => {
        const resource = AvailableSwatches[index];
        if (!resource) {
            return '';
        }
        return IMAGE_URL_PREFIX + resource;
    };

    const renderStyleSwatch = useCallback(() => {
        if (!showSwatches) {
            return <></>;
        }

        return styles.slice(0, STYLE_COUNT_LIMIT).map((style, styleIndex) => (
            <button
                className="product-card__color-and-taste-option"
                key={style.DimensionId}
                onKeyDown={(e) => onColorOrTasteKeyDownListener(e, styleIndex)}
                onClick={(e) => onColorOrTasteClickListener(e, styleIndex)}
                disabled={isStyleDisabled(styleIndex)}
                aria-pressed={styleIndex === selectedColorOrTasteIndex}
            >
                <input
                    type="checkbox"
                    id={`${name}-color-or-taste-${style.DimensionId}`}
                    checked={styleIndex === selectedColorOrTasteIndex}
                    disabled={isStyleDisabled(styleIndex)}
                    onChange={() => setSelectedColorOrTasteIndex(styleIndex)}
                />
                <label
                    className="product-card__color-and-taste-option-label"
                    htmlFor={`${name}-color-or-taste-${style.DimensionId}`}
                    style={{ backgroundImage: `url(${getSwatchUrl(styleIndex)})` }}
                >
                    <span className="visually-hidden">
                        {colorOrTasteText} {style.DisplayName}
                    </span>
                </label>
            </button>
        ));
    }, [
        showSwatches,
        styles,
        STYLE_COUNT_LIMIT,
        onColorOrTasteKeyDownListener,
        onColorOrTasteClickListener,
        isStyleDisabled,
        selectedColorOrTasteIndex,
        setSelectedColorOrTasteIndex,
        name,
        SwatchHelpers,
        SwatchHelpers.getSwatchUrl,
        colorOrTasteText,
    ]);

    const renderStyleDropdown = useCallback(() => {
        if (styles.length === 0) {
            return <></>;
        }

        return (
            <select
                className="product-card__color-and-taste__dropdown"
                defaultValue={styles[firstAvailableStyleIndex].DimensionId}
                onLoad={() => setSelectedColorOrTasteIndex(firstAvailableStyleIndex)}
                onChange={(e) => {
                    const index = styles.findIndex((style) => {
                        return style.DimensionId === e.target.value;
                    });
                    setSelectedColorOrTasteIndex(index);
                }}
                value={styles[selectedColorOrTasteIndex]?.DimensionId}
            >
                {styles.slice(0, STYLE_COUNT_LIMIT).map((style, styleIndex) => (
                    <option key={style.DimensionId} value={style.DimensionId} disabled={isStyleDisabled(styleIndex)}>
                        {style.DisplayName}
                    </option>
                ))}
            </select>
        );
    }, [styles, setSelectedColorOrTasteIndex, selectedColorOrTasteIndex, isStyleDisabled]);

    const renderStyles = () => {
        return (
            <fieldset className="product-card__main__fieldset">
                <legend className="visually-hidden">{colorOrTasteText}</legend>
                {/* Color and Taste */}
                {styles.length !== 0 && (
                    <div className="product-card__color-and-taste-wrapper">
                        <p className="product-card__color-and-taste-title">
                            {t('selectStyle')}:{' '}
                            {selectedColorOrTasteIndex !== -1 && (
                                <strong>{styles[selectedColorOrTasteIndex].DisplayName}</strong>
                            )}
                        </p>
                        {showSwatches && (
                            <div className="product-card__color-and-taste-items">{renderStyleSwatch()}</div>
                        )}
                        {!showSwatches && renderStyleDropdown()}
                        {styles.length >= STYLE_COUNT_LIMIT && (
                            <a
                                href={productHref}
                                className="product-card__color-and-taste-view-all"
                                onClick={
                                    itemNo && !isNaN(position) && currentPage && pageSize
                                        ? () => trackSearchEventClick(itemNo, position)
                                        : null
                                }
                            >
                                {`${t('viewAll')} ${colorOrTasteText}`}
                            </a>
                        )}
                    </div>
                )}
            </fieldset>
        );
    };

    const renderSizeDropdown = useCallback(() => {
        if (sizes.length === 0) {
            return <></>;
        }

        return (
            <select
                className="product-card__color-and-taste__dropdown"
                defaultValue={sizes[firstAvailableSizeIndex].DimensionId}
                onLoad={() => setSelectedSizeIndex(firstAvailableSizeIndex)}
                onChange={(e) => {
                    const index = sizes.findIndex((size) => {
                        return size.DimensionId === e.target.value;
                    });
                    setSelectedSizeIndex(index);
                }}
                value={sizes[selectedSizeIndex]?.DimensionId}
            >
                {sizes.map((size, sizeIndex) => (
                    <option key={size.DimensionId} value={size.DimensionId} disabled={isSizeDisabled(sizeIndex)}>
                        {size.DisplayName}
                    </option>
                ))}
            </select>
        );
    }, [sizes, firstAvailableSizeIndex, selectedSizeIndex, setSelectedSizeIndex, isSizeDisabled]);

    return (
        <>
            <div
                className="product-card__overlay"
                aria-hidden="true"
                onClick={() => {
                    setIsAddedToBag(false);
                    onCloseListener();
                }}
            />
            {/* Mobile Modal */}
            <div
                className="product-card__mobile-modal"
                id="product-card-overlay"
                role="dialog"
                ref={productCardOverlay}
                onKeyDown={(e) => keyboardControls(e)}
            >
                <button
                    className="product-card__mobile-modal__close-button"
                    onClick={() => {
                        setIsAddedToBag(false);
                        onCloseListener();
                    }}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="chevron--close" viewBox="0 0 14 15">
                        <g fill="none" fillRule="evenodd" stroke="none" strokeWidth="1">
                            <g transform="translate(-764 -1012)">
                                <path className="chevron-fill" d="M0 0H1990V6436H0z"></path>
                                <g
                                    stroke="#000"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    transform="translate(413 1002)"
                                >
                                    <g transform="translate(351 10.5)">
                                        <g transform="translate(1.167 1.167)">
                                            <path d="M0.278 0.278L11.389 11.389"></path>
                                            <path
                                                d="M0.278 0.278L11.389 11.389"
                                                transform="matrix(-1 0 0 1 11.667 0)"
                                            ></path>
                                        </g>
                                    </g>
                                </g>
                            </g>
                        </g>
                    </svg>
                </button>

                <form className="product-card__main">
                    <fieldset className="product-card__main__fieldset">
                        <legend className="visually-hidden">{t('sizes')}</legend>

                        {/* Size */}
                        {/* Size */}
                        {displayedSizeLabels.length !== 0 && (
                            <div className="product-card__size-wrapper">
                                <span className="product-card__size-title">
                                    {t('selectSize')}:{' '}
                                    {selectedSizeIndex !== -1 && (
                                        <strong>{displayedSizeLabels[selectedSizeIndex].DisplayName}</strong>
                                    )}
                                </span>
                                <div
                                    className={classNames('product-card__size-items', {
                                        'product-card__size-items--sm': labelSize === 'sm',
                                        'product-card__size-items--md': labelSize === 'md',
                                        'product-card__size-items--lg': labelSize === 'lg',
                                        'product-card__size-items--xl': labelSize === 'xl',
                                    })}
                                >
                                    {labelSize === 'select' && renderSizeDropdown()}
                                    {labelSize !== 'select' &&
                                        displayedSizeLabels.map((size, sizeIndex) => (
                                            <button
                                                className="product-card__size-option"
                                                key={size.DimensionId}
                                                onKeyDown={(e) => onSizeKeyDownListener(e, sizeIndex)}
                                                onClick={(e) => onSizeClickListener(e, sizeIndex)}
                                                disabled={isSizeDisabled(sizeIndex)}
                                                aria-pressed={sizeIndex === selectedSizeIndex}
                                            >
                                                <input
                                                    type="checkbox"
                                                    id={`${name}-size-${size.DimensionId}`}
                                                    checked={sizeIndex === selectedSizeIndex}
                                                    onChange={() => setSelectedSizeIndex(sizeIndex)}
                                                    disabled={isSizeDisabled(sizeIndex)}
                                                    tabIndex={0}
                                                />
                                                <label
                                                    className="product-card__size-option-label"
                                                    htmlFor={`${name}-size-${size.DimensionId}`}
                                                >
                                                    <span className="visually-hidden">Size</span>
                                                    {size.DisplayName}
                                                </label>
                                            </button>
                                        ))}
                                </div>
                                {sizes.length > displayedSizeLabels.length && (
                                    <a
                                        href={productHref}
                                        className="product-card__size-view-all"
                                        onClick={
                                            itemNo && !isNaN(position) && currentPage && pageSize
                                                ? () => trackSearchEventClick(itemNo, position)
                                                : null
                                        }
                                    >
                                        {t('viewAllSizes')}
                                    </a>
                                )}
                            </div>
                        )}
                    </fieldset>

                    {renderStyles()}

                    <fieldset className="product-card__main__fieldset">
                        <legend className="visually-hidden"></legend>

                        {/* Frequency Options */}
                        {isSOEligible && (
                            <div className="product-card__size-wrapper">
                                <span className="product-card__size-title">{t('optionsAvailable')}: </span>
                                <div
                                    className={classNames('product-card__size-items', {
                                        'product-card__size-items--sm': labelSize === 'sm',
                                        'product-card__size-items--md': labelSize === 'md',
                                        'product-card__size-items--lg': labelSize === 'lg',
                                        'product-card__size-items--xl': labelSize === 'xl',
                                    })}
                                >
                                    <div className="product-card__size-option">
                                        <label
                                            className={classNames('product-card__size-option-frequency', {
                                                frequency__disabled: !isFreqAutoDelivery(),
                                            })}
                                        >
                                            {t('autoDelivery')}
                                        </label>
                                    </div>
                                    <div className="product-card__size-option">
                                        <label className="product-card__size-option-frequency">
                                            {t('oneTimeShipment')}
                                        </label>
                                    </div>
                                </div>
                            </div>
                        )}
                    </fieldset>
                </form>

                <button
                    className="product-card__add-button product-card__add-button--modal"
                    disabled={!(buttonText === t('addToBag') || buttonText === t('addedToBag'))}
                    //onClick={onAddToBagClick}
                    onClick={() => {
                        onAddToBagClickListener(selectedColorOrTasteIndex, selectedSizeIndex);
                    }}
                >
                    {(isAddedToBag || mainIsAddedToBag) && (
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="16"
                            viewBox="0 0 20 16"
                            className="product-card__add-button__added-icon"
                        >
                            <g fill="none" fillRule="evenodd" stroke="none" strokeLinecap="round" strokeWidth="1">
                                <g
                                    className="chevron chevron-stroke--white"
                                    strokeWidth="2"
                                    transform="translate(-85 -609)"
                                >
                                    <g transform="translate(13 591)">
                                        <path d="M73 26L79.5454545 32 91 19"></path>
                                    </g>
                                </g>
                            </g>
                        </svg>
                    )}

                    <div
                        className="product-card__add-button__loading"
                        style={{ display: isAddToBagLoading ? 'flex' : 'none' }}
                    >
                        <img src={LOADING_ASSET_PATH} alt="" className="product-card__add-button__loading-icon" />
                    </div>
                    <span className="product-card__add-button-title">{ctaText}</span>
                </button>
            </div>
        </>
    );
};

export default ProductCardModal;
